import React, { useState } from 'react';
import { Grid, Typography, TextField, Button, Paper, Box, CircularProgress } from '@mui/material';

export interface UpdateEmailProps {
	existingEmail?: string;
	onCancel?: () => void;
	onSubmit?: (newEmail: string) => void;
	isLoading?: boolean;
}

const UpdateEmail = (props: UpdateEmailProps) => {
	const { existingEmail, onCancel, onSubmit, isLoading = false } = props;
	const [newEmail, setNewEmail] = useState<string>('');
	const [confirmEmail, setConfirmEmail] = useState<string>('');
	const [newEmailError, setNewEmailError] = useState<string>('');
	const [confirmEmailError, setConfirmEmailError] = useState<string>('');
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	const validateEmail = (email: string): boolean => emailRegex.test(email);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setNewEmail(value);
		// Required validation
		if (!value || value.trim() === '') {
			setNewEmailError('New email is required.');
			return;
		}
		// Format validation
		if (!validateEmail(value)) {
			setNewEmailError('Please enter a valid email address.');
			return;
		}
		setNewEmailError('');
		// Also revalidate confirm
		if (confirmEmail && value !== confirmEmail) {
			setConfirmEmailError('Email addresses do not match.');
		} else {
			setConfirmEmailError('');
		}
	};

	const handleConfirmEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setConfirmEmail(value);
		if (!value || value.trim() === '') {
			setConfirmEmailError('Confirm email is required.');
			return;
		}
		if (value !== newEmail) {
			setConfirmEmailError('Email addresses do not match.');
		} else {
			setConfirmEmailError('');
		}
	};

	const handleSubmit = (): void => {
		// Required checks
		if (!newEmail || newEmail.trim() === '') {
			setNewEmailError('New email is required.');
			return;
		}
		if (!confirmEmail || confirmEmail.trim() === '') {
			setConfirmEmailError('Confirm email is required.');
			return;
		}
		// Format
		if (!validateEmail(newEmail)) {
			setNewEmailError('Please enter a valid email address.');
			return;
		}
		// Match
		if (newEmail !== confirmEmail) {
			setConfirmEmailError('Email addresses do not match.');
			return;
		}

		// Clear errors and submit with lowercase email (with null check)
		setNewEmailError('');
		setConfirmEmailError('');
		onSubmit?.(newEmail ? newEmail.toLowerCase() : '');
	};

	return (
		<Grid container justifyContent="center">
			<Grid item xs={12} sx={{ height: '100%' }}>
				<Paper elevation={3} sx={{ p: 4, borderRadius: 2, height: '100%' }}>
					<Grid container justifyContent="space-between" alignItems="center" direction="row" sx={{ mb: 2 }}>
						<Grid item>
							<Button
								variant="outlined"
								sx={{
									borderColor: '#446388',
									color: '#446388',
									backgroundColor: 'white',
									marginTop: '3%',
									fontSize: '0.7rem',
								}}
								onClick={onCancel}
								disabled={isLoading}
							>
								Back
							</Button>
						</Grid>
						<Grid item>
							<Button
								variant="contained"
								sx={{ backgroundColor: '#446388', marginTop: '3%', fontSize: '0.7rem' }}
								onClick={handleSubmit}
								disabled={isLoading}
								startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
							>
								{isLoading ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
					<Grid container>
						<Grid>
							<Typography variant="h6" gutterBottom sx={{ fontFamily: 'Gotham' }}>
								Update Email Address
							</Typography>
						</Grid>
					</Grid>
					<Box mb={2} mt={1}>
						<Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gotham' }}>
							Current Email:
						</Typography>
						<Typography variant="body1" sx={{ fontFamily: 'Gotham' }}>{existingEmail}</Typography>
					</Box>

					<Grid container spacing={2} mt={5}>
						<Grid item xs={12}>
							<TextField
									label="New Email Address"
									fullWidth
									type="email"
									value={newEmail}
									onChange={handleEmailChange}
									error={Boolean(newEmailError)}
									helperText={newEmailError}
									sx={{ fontFamily: 'Gotham' }}
									disabled={isLoading}
								/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Confirm New Email Address"
								fullWidth
								type="email"
								value={confirmEmail}
								onChange={handleConfirmEmailChange}
								error={Boolean(confirmEmailError)}
								helperText={confirmEmailError}
								sx={{ fontFamily: 'Gotham' }}
								disabled={isLoading}
							/>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default UpdateEmail;
